<main>
  <loading-overlay [isLoading]="isLoading() > 0" />
  <p-toast position="top-center" />

  <div class="content">
    <div class="page__title">
      <div class="page__title_container">
        <h1>{{ 'ExpenseSettlement_Title' | translateWF: 'Expense settlements' }}</h1>
        @if (canSettle) {
          <div class="page__title_buttons">
            <p-button [label]="'ExpenseSettlement_New' | translateWF: 'New settlement'" icon="pi pi-plus" (click)="createSettlement()" />
          </div>
        }
      </div>
      <p class="text-sm text-[var(--p-text-muted-color)]">
        {{ 'ExpenseSettlement_Subtitle' | translateWF: 'One bank movement pays the posted claims of a fund or trip; a trip’s advances net against them.' }}
      </p>
    </div>

    <p-panel [header]="'ExpenseSettlement_Queue' | translateWF: 'Waiting to be settled'" [toggleable]="true" styleClass="mb-5">
      <p-card data-variant="compact">
        <p-table [value]="queue()" [rowHover]="true">
          <ng-template pTemplate="header">
            <tr>
              <th>{{ 'ExpenseSettlement_Field_Owner' | translateWF: 'Fund or trip' }}</th>
              <th class="text-right" style="width: 8rem">{{ 'ExpenseSettlement_Col_Claims' | translateWF: 'Claims' }}</th>
              <th class="text-right" style="width: 12rem">{{ 'ExpenseSettlement_Col_Outstanding' | translateWF: 'Outstanding' }}</th>
              @if (canSettle) {
                <th style="width: 9rem"></th>
              }
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-rowData>
            @let row = getQueueRow(rowData);
            <tr>
              <td>
                <div class="flex items-center gap-2">
                  <i [class]="row.owner.entityDefinitionCode === ownerCodes.Trip ? 'pi pi-briefcase' : 'pi pi-wallet'" class="text-surface-500" aria-hidden="true"></i>
                  <span class="flex flex-col">
                    <span>{{ row.name }}</span>
                    <small class="text-surface-500 font-mono">{{ row.owner.number }}</small>
                  </span>
                </div>
              </td>
              <td class="text-right tabular-nums">{{ row.claims }}</td>
              <td class="text-right tabular-nums">{{ symbolOf(row.currency) }} {{ row.outstanding | number: '1.2-2' }}</td>
              @if (canSettle) {
                <td class="text-right">
                  <p-button [label]="'ExpenseSettlement_Settle' | translateWF: 'Settle'" icon="pi pi-arrow-right" size="small" variant="outlined" (onClick)="settle(row)" />
                </td>
              }
            </tr>
          </ng-template>
          <ng-template pTemplate="emptymessage">
            <tr>
              <td [attr.colspan]="canSettle ? 4 : 3" class="text-center">{{ 'ExpenseSettlement_Queue_Empty' | translateWF: 'No posted claims are waiting to be settled.' }}</td>
            </tr>
          </ng-template>
        </p-table>
      </p-card>
    </p-panel>

    <div class="cs-page-list-grid">
      @if (showFilters() && filterConfig(); as config) {
        <div>
          <csl-dynamic-filters [filterConfig]="config" [state]="filterState" />
        </div>
      }

      <p-panel [header]="'ExpenseSettlement_Title' | translateWF: 'Expense settlements'">
        <ng-template pTemplate="icons">
          <p-button
            [label]="(showFilters() ? 'Common_Hide_Filters' : 'Common_Show_Filters') | translateWF: (showFilters() ? 'Hide Filters' : 'Show Filters')"
            [icon]="showFilters() ? 'pi pi-filter-slash' : 'pi pi-filter'"
            (click)="showFilters.set(!showFilters())"
            variant="outlined"
            size="small"
          />
        </ng-template>
        <p-card data-variant="compact">
          <p-table [value]="items()" dataKey="id" [lazy]="true" (onSort)="onSort($event)" [rowHover]="true">
            <ng-template pTemplate="header">
              <tr>
                <th pSortableColumn="number" style="width: 14rem">
                  {{ 'ExpenseSettlement_Col_Number' | translateWF: 'Number' }}
                  <p-sortIcon field="number" />
                </th>
                <th pSortableColumn="owner">
                  {{ 'ExpenseSettlement_Field_Owner' | translateWF: 'Fund or trip' }}
                  <p-sortIcon field="owner" />
                </th>
                <th pSortableColumn="date" style="width: 9rem">
                  {{ 'ExpenseSettlement_Field_Date' | translateWF: 'Date' }}
                  <p-sortIcon field="date" />
                </th>
                <th class="text-right" style="width: 7rem">{{ 'ExpenseSettlement_Col_Claims' | translateWF: 'Claims' }}</th>
                <th class="text-right" style="width: 12rem">{{ 'ExpenseSettlement_Col_Net' | translateWF: 'Net' }}</th>
                <th pSortableColumn="status" style="width: 9rem">
                  {{ 'Status' | translateWF: 'Status' }}
                  <p-sortIcon field="status" />
                </th>
              </tr>
            </ng-template>

            <ng-template pTemplate="body" let-rowData>
              @let row = getRow(rowData);
              <tr>
                <td>
                  <a class="cs-table-link font-mono" (click)="redirectToDetails(row.id)">{{ row.number }}</a>
                </td>
                <td>
                  <div class="flex items-center gap-2">
                    <i [class]="row.owner.entityDefinitionCode === ownerCodes.Trip ? 'pi pi-briefcase' : 'pi pi-wallet'" class="text-surface-500" aria-hidden="true"></i>
                    <span class="flex flex-col">
                      <span>{{ row.ownerName }}</span>
                      <small class="text-surface-500 font-mono">{{ row.owner.number }}</small>
                    </span>
                  </div>
                </td>
                <td>{{ row.settlementDate | date: 'dd/MM/yyyy' : 'UTC' }}</td>
                <td class="text-right tabular-nums">{{ row.claims.length }}</td>
                <td class="text-right tabular-nums">
                  @if (row.direction === directions.Incoming) {
                    <i class="pi pi-arrow-down-left text-green-600 mr-1" aria-hidden="true"></i>
                  } @else if (row.direction === directions.Outgoing) {
                    <i class="pi pi-arrow-up-right text-surface-500 mr-1" aria-hidden="true"></i>
                  }
                  {{ symbolOf(row.currency) }} {{ row.net | number: '1.2-2' }}
                </td>
                <td>
                  <p-tag [severity]="statusSeverity(row.status)" [value]="statusLabel(row.status)" />
                </td>
              </tr>
            </ng-template>

            <ng-template pTemplate="emptymessage">
              <tr>
                <td colspan="6" class="text-center">{{ 'ExpenseSettlement_Empty' | translateWF: 'No expense settlements found.' }}</td>
              </tr>
            </ng-template>
          </p-table>
          <p-paginator
            [rows]="pageSize()"
            [totalRecords]="totalRecords()"
            [first]="(currentPage() - 1) * pageSize()"
            (onPageChange)="onPageChange($event)"
            [rowsPerPageOptions]="[25, 50, 100]"
          />
        </p-card>
      </p-panel>
    </div>
  </div>
</main>
